import React, { useState } from 'react';
import { PiggyBank, GraduationCap, Stethoscope } from 'lucide-react';
import { requestNotificationPermission } from '../utils/notifications';
import { toast } from 'react-hot-toast';

interface ModeSelectorProps {
  onModeChange: (mode: 'default' | 'finance' | 'education-beta' | 'direct-care') => void;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ onModeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'default' | 'finance' | 'education-beta' | 'direct-care'>('default');
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;
  const [showFinanceModal, setShowFinanceModal] = useState(false);

  const handleModeChange = (mode: 'default' | 'finance' | 'education-beta' | 'direct-care') => {
    if (mode === 'finance') {
      setShowFinanceModal(true);
      return;
    }

    if (mode === 'education-beta') {
      const handleRedirection = () => {
        localStorage.setItem('lastMode', selectedMode);
        window.location.href = 'https://genuine-pithivier-a5534e.netlify.app/';
      };

      const confirmed = window.confirm(
        'You are about to leave this page and be redirected to Education 1.0 (Beta). Are you sure you want to continue?'
      );

      if (confirmed) {
        if (isPWA) {
          requestNotificationPermission().then(granted => {
            if (granted) {
              toast.success('Notifications enabled for Education 1.0 Beta');
            }
            handleRedirection();
          });
        } else {
          handleRedirection();
        }
      }
      return;
    }

    setSelectedMode(mode);
    onModeChange(mode);
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  const modeButtonClasses = (mode: string) => `w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
    selectedMode === mode
      ? mode === 'default'
        ? 'bg-blue-600 text-white shadow-[0_0_10px_rgba(37,99,235,0.3)]'
        : mode === 'finance'
        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
        : mode === 'education'
        ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
        : 'bg-gradient-to-r from-teal-500 to-teal-600 text-white'
      : 'text-gray-700 hover:bg-gray-100 transition-colors'
  }`;

  return (
    <div className="absolute top-[max(env(safe-area-inset-top),1rem)] left-4 pt-4 z-[200]">
      <button
        onClick={toggleMenu}
        className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-all relative overflow-hidden border backdrop-blur-sm group ${
          selectedMode === 'default'
            ? 'bg-white/20 border-white/40 shadow-[0_0_15px_rgba(255,255,255,0.3)]'
            : selectedMode === 'education'
            ? 'bg-purple-500/20 border-purple-400/40 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
            : selectedMode === 'direct-care'
            ? 'bg-teal-500/20 border-teal-400/40 shadow-[0_0_15px_rgba(45,212,191,0.3)]'
            : 'bg-white/20 border-white/40 shadow-[0_0_15px_rgba(255,255,255,0.3)]'
        }`}
        aria-label="Change mode"
      >
        {selectedMode === 'default' ? (
          <div className="h-full w-full flex items-center justify-center">
            <div className="relative">
              <div className="w-4 h-4 rounded-full bg-white z-10 relative" />
              <div className="absolute inset-0 w-4 h-4 rounded-full bg-white/50 animate-glow-pulse" />
            </div>
          </div>
        ) : selectedMode === 'education' ? (
          <GraduationCap className="h-5 w-5 transition-opacity" />
        ) : selectedMode === 'direct-care' ? (
          <Stethoscope className="h-5 w-5 text-teal-300 transition-opacity" />
        ) : (
          <PiggyBank className="h-5 w-5 transition-opacity" />
        )}
      </button>

      {/* Finance Coming Soon Modal */}
      {showFinanceModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">Finance Mode Coming Soon!</h3>
            <p className="text-gray-600">
              We're working hard to bring you advanced financial tools and insights. Stay tuned for updates!
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowFinanceModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[201] bg-transparent"
            onClick={toggleMenu}
          />
          <div className="absolute top-full left-0 mt-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-2 min-w-[180px] z-[202] animate-fade-in">
            <div className="space-y-1">
              <button
                onClick={() => handleModeChange('default')}
                className={modeButtonClasses('default')}
              >
                <div className="relative">
                  <div className="w-4 h-4 rounded-full bg-current" />
                  {selectedMode === 'default' && (
                    <div className="absolute inset-0 w-4 h-4 rounded-full bg-current opacity-50 animate-glow-pulse" />
                  )}
                </div>
                Angel 1.0
              </button>
              <button
                onClick={() => handleModeChange('finance')}
                className={modeButtonClasses('finance')}
                title="Coming Soon"
              >
                <PiggyBank className="h-4 w-4" />
                Finance <span className="text-xs text-yellow-500 ml-1">(Coming Soon)</span>
              </button>
              <button
                onClick={() => handleModeChange('direct-care')}
                className={modeButtonClasses('direct-care')}
              >
                <Stethoscope className="h-4 w-4" />
                Direct Care
              </button>
              <button
                onClick={() => handleModeChange('education-beta')}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <GraduationCap className="h-4 w-4 text-yellow-500" />
                    <span>Education 1.0 </span>
                    <span className="text-yellow-500 text-xs">(Beta)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0.5 rounded-full animate-pulse">
                      New
                    </span>
                    {isPWA && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">
                        PWA
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModeSelector;
